// Automatic retry for the manual crash cash-out POST.
//
// On the PRIMARY widget domain (game.marbles.xyz) getCashoutApiBases returns a
// SINGLE base, so useCashout's per-base loop ran exactly once, UNBOUNDED, and
// rethrew on the first fetch failure. One transient network blip was terminal
// and the player became the retry loop by hand — 40 taps across two rounds
// (hub88 Acebet, 2026-09-04); ~100 sessions / ~1,600 failed taps across 6+
// operators in the prior 8 days. CloudFront logged the CORS preflight for each
// bet and ZERO POSTs: the path worked seconds earlier, the POST specifically
// died. The v1.0.191 mirror fallback structurally cannot help here — there is
// no second base on the primary. This is the retry that note prescribed.
//
// The retry is deliberately NARROW:
//
//   - It retries ONLY when fetch THROWS (DNS, TLS, connection reset, timeout
//     abort). A Response of ANY status — 2xx or 4xx/5xx — means the server
//     answered and is returned untouched. Re-sending after a 409/400 is
//     pointless; re-sending after a 2xx is the double-submit this must never do.
//
//   - It is money-safe even when a timed-out POST DID reach the server. The Go
//     cashout use case rejects a non-pending bet (BetNotPendingForCashoutError,
//     mapped to 'not-pending') and the DynamoDB pending->cashed_out write is
//     CONDITIONAL, so only ONE transition can ever win. A retry after a silent
//     success gets 'not-pending' and the panel reconciles via fetchBets.
//
//   - EVERY attempt is bounded, including the last one on the last base. The
//     old loop left the last base unbounded "to keep pre-existing behaviour",
//     which on the primary meant a blackholed POST ate the whole cash-out
//     window with nothing to show for it.
//
// Pure and dependency-free (fetch, sleep and the timeout signal are injectable)
// so the policy is unit-tested without timers or Datadog. See cashoutRetry.test.ts.

export interface RetryPolicy {
  // Total attempts, including the first. 1 = plain fetch, no retry.
  attempts: number
  // Per-attempt bound. A fresh AbortSignal.timeout is attached to EVERY attempt.
  timeoutMs: number
  // Delay before attempt n+1, indexed by n-1. Shorter than `attempts - 1` is
  // fine — the last value repeats.
  backoffMs: number[]
}

// Worst case ≈ 3 × 2.5 s + 0.45 s ≈ 8 s, inside a crash round. Typical cost is
// milliseconds: the failures this targets fail FAST (the player's taps landed
// 240–400 ms apart), so the bound only matters for the blackhole case.
export const CASHOUT_RETRY: RetryPolicy = {
  attempts: 3,
  timeoutMs: 2500,
  backoffMs: [150, 300]
}

export type FetchLike = (input: string, init: RequestInit) => Promise<Response>

export interface RetryInfo {
  // 1-based index of the attempt that just failed.
  attempt: number
  error: unknown
  delayMs: number
}

export interface RetryHooks {
  // Fired before each retry — the telemetry seam (cashout_retry).
  onRetry?: (info: RetryInfo) => void
  sleep?: (ms: number) => Promise<void>
  makeSignal?: (ms: number) => AbortSignal | undefined
}

const defaultSleep = (ms: number): Promise<void> =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms)
  })

// Guarded exactly as the old inline code was: an environment without
// AbortSignal.timeout gets an unbounded attempt rather than a crash.
const defaultMakeSignal = (ms: number): AbortSignal | undefined =>
  typeof AbortSignal !== 'undefined' && 'timeout' in AbortSignal ? AbortSignal.timeout(ms) : undefined

export async function fetchWithNetworkRetry(
  fetchImpl: FetchLike,
  url: string,
  init: RequestInit,
  policy: RetryPolicy,
  hooks: RetryHooks = {}
): Promise<Response> {
  const sleep = hooks.sleep ?? defaultSleep
  const makeSignal = hooks.makeSignal ?? defaultMakeSignal
  const attempts = Math.max(1, policy.attempts)
  let lastError: unknown

  for (let attempt = 1; attempt <= attempts; attempt++) {
    try {
      // Object.assign rather than spread: the widget compiles to es5 and this
      // file must never be the reason a release fails to build.
      const attemptInit = Object.assign({}, init, { signal: makeSignal(policy.timeoutMs) })
      // Any Response — including 4xx/5xx — is a server answer. Return it.
      return await fetchImpl(url, attemptInit)
    } catch (err) {
      lastError = err
      if (attempt === attempts) break
      const backoff = policy.backoffMs
      const delayMs = backoff.length === 0 ? 0 : backoff[Math.min(attempt - 1, backoff.length - 1)] ?? 0
      if (hooks.onRetry) hooks.onRetry({ attempt, error: err, delayMs })
      await sleep(delayMs)
    }
  }

  throw lastError
}
